import React, { useEffect, useRef, useState } from 'react';
import devCodingSession from '../assets/images/echoes/echoes-dev-coding-session.webp';
import devTeamCollaboration from '../assets/images/echoes/echoes-dev-team-collaboration.webp';
import devWorkroomOverview from '../assets/images/echoes/echoes-dev-workroom-overview.webp';
import devTeamShowcase from '../assets/images/echoes/echoes-dev-team-showcase.webp';
import devLevelReview from '../assets/images/echoes/echoes-dev-level-review.webp';
import devCoffeeAndCode from '../assets/images/echoes/echoes-dev-coffee-and-code.webp';
import devStudioPortrait from '../assets/images/echoes/echoes-dev-studio-portrait.webp';
import heroBackground from '../assets/images/echoes/echoes-hero-background.webp';
import titleLogo from '../assets/images/echoes/echoes-dev-title-hero.webp';
import bgMusic from '../assets/sounds/echoes-bg-music.mp3';
import '../styles/EchoesPromo.css';

const mechanics = [
  'Exploration',
  'Clue gathering',
  'Puzzle solving',
  'Stealth / hiding',
  'Story-driven progression',
];

const callsToAction = [
  'Follow the game',
  'Watch the trailer',
  'Support the project',
  'Try the demo if available',
];

const galleryItems = [
  {
    title: 'Development Workspace',
    caption: 'Core collaboration and setup during active development.',
    image: devWorkroomOverview,
  },
  {
    title: 'Team Coordination',
    caption: 'Reviewing progress, testing builds, and aligning tasks together.',
    image: devTeamCollaboration,
  },
  {
    title: 'Late-Night Build Session',
    caption: 'Hands-on iteration as the team shapes Echoes of Unforgatten.',
    image: devCodingSession,
  },
  {
    title: 'Team Showcase',
    caption: 'The crew presenting Echoes of Unforgatten alongside Logica Technology.',
    image: devTeamShowcase,
  },
  {
    title: 'Level Design Review',
    caption: 'Walking through scene layouts and gameplay flow on the Godot editor.',
    image: devLevelReview,
  },
  {
    title: 'Coffee & Code',
    caption: 'Long hours fueled by caffeine — debugging AI patrol logic in Godot.',
    image: devCoffeeAndCode,
  },
  {
    title: 'Studio Portrait',
    caption: 'A moment with the team inside the Logica Technology workroom.',
    image: devStudioPortrait,
  },
];

const teamCredits = [
  {
    name: 'Allen Tumbagahan',
    role: 'Project Director · Story Writer · Lead Developer · 3D Modeler · Environment Designer',
  },
  { name: 'Joichilo Funabashi', role: 'Game Developer' },
  { name: 'Jayrell Llagas', role: 'Game UI Designer' },
  { name: 'Jayson Delica', role: 'Sound Designer' },
  { name: 'John Russel Siasat', role: '3D Assets Artist' },
  { name: 'Martin Shealtiel Alvarez', role: 'Technical Writer' },
  { name: 'Glen Custodio Posadas', role: 'Technical Writer' },
];

const toolsUsed = [
  { name: 'MakeHuman Community', description: 'Free 3D Character Builder' },
  { name: 'Mixamo', description: 'Free Character Animations' },
];

const specialThanks = [
  { name: 'Mr. Kervin Draude Ilao Corpuz', role: 'Professor · Project Adviser' },
  { name: 'Sir John Robillos', role: 'Project Support · Adviser' },
];

const EchoesPromo = () => {
  const [isTrailerOpen, setIsTrailerOpen] = useState(false);
  const [activeImage, setActiveImage] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const openTrailer = () => setIsTrailerOpen(true);
  const closeTrailer = () => setIsTrailerOpen(false);
  const openImage = (item) => setActiveImage(item);
  const closeImage = () => setActiveImage(null);

  const isAnyModalOpen = isTrailerOpen || activeImage !== null;

  // Attempt to autoplay on mount. If the browser blocks autoplay with sound,
  // fall back to a muted state so the user can unmute on first interaction.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = false;
    const playPromise = audio.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {
        audio.muted = true;
        setIsMuted(true);
      });
    }
  }, []);

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !isMuted;
    setIsMuted(next);
    audio.muted = next;
    if (!next) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  };

  // Pause music while a modal is open, resume when closed (if not muted).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isAnyModalOpen) {
      audio.pause();
    } else if (!isMuted) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    }
  }, [isAnyModalOpen, isMuted]);

  useEffect(() => {
    if (!isAnyModalOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        closeTrailer();
        closeImage();
      }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isAnyModalOpen]);

  return (
    <div className="echoes-promo">
      <audio ref={audioRef} src={bgMusic} loop preload="auto" />
      <button
        type="button"
        className={`echoes-music-toggle${isMuted ? '' : ' is-playing'}`}
        onClick={toggleMute}
        aria-label={isMuted ? 'Unmute background music' : 'Mute background music'}
        aria-pressed={!isMuted}
      >
        {isMuted ? (
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M3.5 9v6h4l5 4V5l-5 4h-4zm13.5 3a4 4 0 0 0-2-3.46v6.92A4 4 0 0 0 17 12zM4.27 3 3 4.27 7.73 9H3.5v6h4l5 4v-5.73L16.73 17a6.93 6.93 0 0 1-1.73.46v2.02a8.94 8.94 0 0 0 3.16-1.05l2.84 2.84L22 17l-1.73-1.73L4.27 3z"
            />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              fill="currentColor"
              d="M3 9v6h4l5 4V5L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.06A4.5 4.5 0 0 0 16.5 12zM14 3.23v2.06A6.99 6.99 0 0 1 19 12a7 7 0 0 1-5 6.71v2.06A9 9 0 0 0 14 3.23z"
            />
          </svg>
        )}
        <span className="echoes-music-label">{isMuted ? 'Play Music' : 'Mute'}</span>
      </button>
      <div className="echoes-card">
        <section className="echoes-hero">
          <img className="echoes-hero-background" src={heroBackground} alt="Echoes of Unforgatten hero background" />
          <div className="echoes-hero-overlay" aria-hidden="true" />
          <div className="echoes-hero-content">
            <div className="echoes-title-frame">
              <h1 className="echoes-hero-title">
                <img
                  src={titleLogo}
                  alt="Echoes of Unforgatten"
                  className="echoes-title-logo"
                  draggable="false"
                />
              </h1>
            </div>
            <p className="echoes-tagline">Some memories never stay buried.</p>
            <p className="echoes-hook">
              A story-driven psychological horror experience about guilt, family tragedy, and the echoes of the past.
            </p>
            <div className="echoes-hero-actions">
              <button
                type="button"
                className="echoes-trailer-button echoes-trailer-button-primary"
                onClick={openTrailer}
              >
                Watch Trailer
              </button>
              <a
                className="echoes-trailer-button echoes-trailer-button-secondary"
                href="https://logicatechnology.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Visit our Website
              </a>
            </div>
          </div>
        </section>

        <section className="echoes-thankyou">
          <p>
            Thank you for taking the time to experience <strong>Echoes of Unforgatten</strong>. Your support,
            attention, and presence mean a lot to our team as we share this project with the audience,
            event organizers, judges, gamers, and supporters who made room for our story.
          </p>
          <p>
            We deeply appreciate everyone who viewed the project and helped carry this vision forward.
          </p>
        </section>

        <section className="echoes-main-flow">
          <article className="echoes-block">
            <span className="echoes-label">Story Overview</span>
            <h2>A boy trapped in the echoes of a broken family</h2>
            <p>
              Step into a broken home haunted by memory, silence, and buried truth. The game follows a boy
              trapped in the echoes of a shattered family, where hidden truths and past sins return through horror.
            </p>
            <p>
              As players move deeper into the house, they uncover a family consumed by jealousy, grief, and the
              consequences of what was left unspoken.
            </p>
          </article>

          <article className="echoes-block echoes-block-mechanics">
            <h2>Explore. Investigate. Hide. Solve. Survive.</h2>
            <ul className="echoes-list echoes-mechanics-grid">
              {mechanics.map((mechanic) => (
                <li key={mechanic}>{mechanic}</li>
              ))}
            </ul>
          </article>

          <article className="echoes-block echoes-block-theme">
            <h2>What silence destroys</h2>
            <p>
              Jealousy, guilt, silence, and hidden pain can destroy a family. Truth must be faced before healing can begin.
            </p>
          </article>

          <article className="echoes-block echoes-block-trailer">
            <div className="echoes-trailer-header">
              <h2>A glimpse into the haunting</h2>
              <p className="echoes-trailer-lede">
                Step inside the world of <strong>Echoes of Unforgatten</strong> — a story shaped by guilt,
                memory, and the things a family chose never to speak of.
              </p>
            </div>
            <div className="echoes-trailer-stage">
              <div className="echoes-video-frame">
                <div className="echoes-video-embed">
                  <iframe
                    className="echoes-video-iframe echoes-video-iframe-desktop"
                    src="https://www.facebook.com/plugins/video.php?height=500&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1636665647640488%2F&show_text=false&width=560&t=0"
                    style={{ border: 'none', overflow: 'hidden' }}
                    scrolling="no"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    allowFullScreen
                    title="Echoes of Unforgatten — Official Trailer"
                  />
                  <iframe
                    className="echoes-video-iframe echoes-video-iframe-mobile"
                    src="https://www.facebook.com/plugins/video.php?height=314&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1636665647640488%2F&show_text=false&width=560&t=0"
                    style={{ border: 'none', overflow: 'hidden' }}
                    scrolling="no"
                    frameBorder="0"
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    allowFullScreen
                    title="Echoes of Unforgatten — Official Trailer"
                  />
                </div>
              </div>
              <aside className="echoes-trailer-meta">
                <ul className="echoes-meta-list">
                  <li>
                    <span className="echoes-meta-key">Genre</span>
                    <span className="echoes-meta-value">Psychological Horror</span>
                  </li>
                  <li>
                    <span className="echoes-meta-key">Studio</span>
                    <span className="echoes-meta-value">Logica Technology</span>
                  </li>
                  <li>
                    <span className="echoes-meta-key">Status</span>
                    <span className="echoes-meta-value">In Development</span>
                  </li>
                  <li>
                    <span className="echoes-meta-key">Runtime</span>
                    <span className="echoes-meta-value">Trailer — 1:45</span>
                  </li>
                </ul>
                <p className="echoes-trailer-note">
                  “Echoes were never meant to stay — but you chose to listen.”
                </p>
              </aside>
            </div>
          </article>

          <article className="echoes-block echoes-block-gallery">
            <h2>Behind the making of the project</h2>
            <p>
              A glimpse into the team behind the build, capturing the collaboration, testing, and work sessions
              that continue to shape the game.
            </p>
          </article>

          <div className="echoes-marquee-wrapper" aria-label="Development gallery">
            <div className="echoes-marquee-track">
              {[...galleryItems, ...galleryItems].map((item, index) => (
                <figure key={`${item.title}-${index}`} className="echoes-gallery-card">
                  <button
                    type="button"
                    className="echoes-gallery-trigger"
                    onClick={() => openImage(item)}
                    aria-label={`View ${item.title}`}
                  >
                    <img
                      className="echoes-gallery-image"
                      src={item.image}
                      alt={item.title}
                      loading="lazy"
                      draggable="false"
                    />
                  </button>
                  <figcaption>
                    <strong>{item.title}</strong>
                    <p>{item.caption}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>

          <article className="echoes-block echoes-block-cta">
            <h2>Stay with the journey</h2>
            <ul className="echoes-list echoes-list-cta">
              {callsToAction.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>

          <article className="echoes-block echoes-block-credits">
            <div className="echoes-credits-header">
              <span className="echoes-label">Credits</span>
              <h2>The voices behind the silence</h2>
              <p className="echoes-credits-lede">
                A heartfelt acknowledgment to the team, sponsors, and mentors who helped
                shape <strong>Echoes of Unforgatten</strong>.
              </p>
            </div>

            <div className="echoes-credits-grid">
              <section className="echoes-credits-group echoes-credits-group-team">
                <h3>Development Team</h3>
                <ul className="echoes-credits-list">
                  {teamCredits.map((member) => (
                    <li key={member.name}>
                      <span className="echoes-credit-name">{member.name}</span>
                      <span className="echoes-credit-role">{member.role}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="echoes-credits-group">
                <h3>Tools &amp; Software</h3>
                <ul className="echoes-credits-list">
                  {toolsUsed.map((tool) => (
                    <li key={tool.name}>
                      <span className="echoes-credit-name">{tool.name}</span>
                      <span className="echoes-credit-role">{tool.description}</span>
                    </li>
                  ))}
                </ul>
              </section>

              <section className="echoes-credits-group">
                <h3>Special Thanks</h3>
                <ul className="echoes-credits-list">
                  {specialThanks.map((person) => (
                    <li key={person.name}>
                      <span className="echoes-credit-name">{person.name}</span>
                      <span className="echoes-credit-role">{person.role}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <p className="echoes-credits-powered">
              Powered by{' '}
              <a
                href="https://logicatechnology.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Logica Technology
              </a>
            </p>
          </article>
        </section>

        <footer className="echoes-footer-bar">
          <span>Logica Technology</span>
          <span>Demo in development</span>
          <span>@EchoesGame</span>
          <span>logicatechnology.com</span>
        </footer>
      </div>

      {isTrailerOpen && (
        <div
          className="echoes-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Echoes of Unforgatten trailer"
          onClick={closeTrailer}
        >
          <div className="echoes-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="echoes-modal-close"
              onClick={closeTrailer}
              aria-label="Close trailer"
            >
              &times;
            </button>
            <div className="echoes-modal-video">
              <iframe
                src="https://www.facebook.com/plugins/video.php?height=314&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F1636665647640488%2F&show_text=false&width=560&t=0&autoplay=1"
                style={{ border: 'none', overflow: 'hidden' }}
                scrolling="no"
                frameBorder="0"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                title="Echoes of Unforgatten — Official Trailer"
              />
            </div>
          </div>
        </div>
      )}

      {activeImage && (
        <div
          className="echoes-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={activeImage.title}
          onClick={closeImage}
        >
          <div className="echoes-modal echoes-modal-image" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="echoes-modal-close"
              onClick={closeImage}
              aria-label="Close image"
            >
              &times;
            </button>
            <img
              className="echoes-modal-img"
              src={activeImage.image}
              alt={activeImage.title}
              draggable="false"
            />
            <div className="echoes-modal-caption">
              <strong>{activeImage.title}</strong>
              <p>{activeImage.caption}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EchoesPromo;